/**
 * 능력치 화면. 장수·동료·병사 한 명 한 명의 체력·공격·방어가 몇인지 본다.
 *
 * 병사가 레벨로 자라고 장비가 몸을 바꾸는데, 그 결과를 숫자로 볼 곳이 없었다. 출정 화면은
 * 레벨과 경험치 막대만, 장비 화면은 전투력 한 숫자만 보였다(플레이한 사람이 짚었다).
 *
 * 숫자는 전장이 세우는 몸을 그대로 읽는다 — 장수는 `dressLeader`, 동료는 `dressCompanion`에
 * `underCommand`, 병사는 `specForTroop`(정병). 화면이 따로 셈하면 전장과 한 칸씩 어긋나도
 * 알 길이 없다. 처음에는 몸만 전장 식으로 읽고 분대 보정은 정의(`captainText`)로 적어,
 * 장비가 주는 지휘 몫이 카드에서 빠졌다(리뷰가 짚었다). 분대 보정은 누구 밑에 서느냐로
 * 바뀌므로 병사 몸에 섞지 않고 지휘관 카드에 적는다.
 *
 * **탭 둘(장수·동료 / 병사)과 한 사람씩 보기**(Todo11). 처음에는 장수·동료 카드를 모두 펼치고 그 아래 장수 기술, 그 아래
 * 병사 표를 한 줄로 늘어놓아 화면이 길었다. 플레이한 사람이 청했다: "장수와 동료는 모두 펼쳐두지말고 선택해서 보는
 * 방식으로. 장수 기술도 장수를 선택했을 때만 보이게. 장수와 동료를 보는 탭과, 병사를 보는 탭으로 나누기."
 *
 * **인물을 그리고, 숫자를 읽히게 짠다**(2026-10-07). 플레이한 사람이 청했다: "능력치, 장수, 부하장수, 병사 화면에 각각의
 * 인물들을 그려줘", "이 창 자체가 개발 버전 같은 느낌이데, 상용 게임 느낌이 날 정도로 개선해줘." 그 전에는 글자 단추로
 * 사람을 고르고, 숫자 여덟을 이름표와 나란히 적은 표 한 칸과 설명 줄 대여섯이 전부였다. 이제 —
 * - 볼 사람은 **얼굴 칩**(가슴 위 그림 + 이름 + 레벨)으로 고르고, 고른 사람은 전신 그림을 무대에 세운다(전장과 같은 차림).
 * - 능력치는 **타일 여덟**이다: 이름표, 큰 숫자, 그 아래 막대. 막대는 장수와 만난 부하장수 가운데 가장 높은 값에 견준
 *   길이라, 누가 무엇에 센지 한눈에 읽힌다(공격 간격만은 짧을수록 길다).
 * - 설명 줄은 "무엇: 내용"으로 갈라 이름표를 단다(`noteParts`) — 줄글 대여섯이 한 덩어리로 보였다.
 * - 병사 표는 그림을 달고, 병종 칩으로 걸러 본다.
 */
import { COMPANION_LORE } from './companion-lore.ts';
import { TERRITORY_BY_ID } from '../meta/realm.ts';
import { equipOf, holdsBow, isMounted, shieldBlocks, shoots } from '../sim/equipment.ts';
import { salaryFloor, salarySteps } from '../meta/run.ts';
import type { Loadout } from '../sim/equipment.ts';
import type { CaptainBonus, CompanionDef, Stats, Weapon } from '../sim/troops.ts';
import {
  favorAt,
  LEVEL_MAX,
  ELITE,
  HERO_WEAPON,
  WEAPONS,
  BRANCH_INSTINCT,
  Branch,
  SHIELD_PENALTY,
  arrowsFor,
  shieldFor,
  CULTURE_NAMES,
  NO_CAPTAIN,
  TROOP_BY_ID,
  clampCaptain,
  fieldHp,
  pacedSpeed,
  soldierGrowth,
  specForTroop,
} from '../sim/troops.ts';
import { dressCompanion, dressLeader, underCommand } from '../sim/scenario.ts';
import {
  companionLevelOf,
  companionXpToNext,
  heroLevelMerit,
  heroLevelOf,
  heroProgress,
  isElite,
  metCompanions,
  RANKS,
  SALARY_DEPTH,
  rankFor,
  soldierLevel,
} from '../meta/army.ts';
import type { Army, Soldier } from '../meta/army.ts';
import { levelProgress, rosterOrder } from '../meta/roster.ts';
import { HERO, loadoutOf } from '../meta/stash.ts';
import { button, clear, el, withKey } from './dom.ts';
import { branchIcon, hasBranchIcon } from './branch-icon.ts';
import { drawUnitPortrait, iconKeyFor, unitIcon } from './unit-icon.ts';
import { MATE_SKILL_BY_ID, MATE_SKILL_LEVELS, MATE_SKILL_LEVEL_MAX, mateSkillLevel, mateSkillText } from '../sim/mate-skills.ts';
import { SKILLS, SKILL_LEVEL_MAX, SKILL_POINT_EVERY, skillPointsFor } from '../sim/skills.ts';
import {
  SKILL_RESET_GOLD,
  freeSkillPoints,
  pickSkill,
  raiseSkill,
  resetSkills,
  skillForBattle,
  skillLevelOf,
  spentSkillPoints,
} from '../meta/skill.ts';
import { favorText, commandText } from './labels.ts';
import { GRADE_RANK_FROM, gapText, growthGrade } from './growth-grade.ts';
import type { GrowthStat } from './growth-grade.ts';

/** 표와 카드가 같은 순서·이름을 쓴다. */
const COLUMNS: readonly { key: string; label: string; title: string; value: (s: Stats) => string }[] = [
  { key: 'hp', label: '체력', title: '체력', value: (s) => String(s.hp) },
  { key: 'atk', label: '공격', title: '한 번 때리는 힘', value: (s) => String(s.atk) },
  { key: 'def', label: '방어', title: '맞을 때마다 깎는 양', value: (s) => String(s.def) },
  { key: 'pierce', label: '관통', title: '상대 방어를 이만큼 무시한다', value: (s) => String(s.pierce) },
  { key: 'range', label: '사거리', title: '픽셀', value: (s) => String(s.range) },
  // 전장의 실제 걸음(걸음 배율을 곱한 값)을 적는다. 표의 값을 그대로 적으면 "초당 픽셀"이 거짓이 된다.
  { key: 'speed', label: '이동', title: '초당 픽셀', value: (s) => String(pacedSpeed(s.speed)) },
  // 30틱이 1초다. 틱으로 적으면 "30"이 빠른지 느린지 읽히지 않는다.
  { key: 'cd', label: '간격', title: '공격 간격(초)', value: (s) => `${(s.cd / 30).toFixed(1)}초` },
  { key: 'grit', label: '사기', title: '높을수록 늦게 무너진다', value: (s) => String(s.grit) },
];

/** 화면의 탭. */
type StatsTab = 'leaders' | 'soldiers';

export class StatsScreen {
  private readonly root: HTMLElement;
  private readonly army: Army;
  private readonly onBack: () => void;
  private readonly onChange: () => void;
  private tab: StatsTab = 'leaders';
  /** 장수·동료 탭에서 보는 사람: `HERO` 또는 동료 id. */
  private who: string = HERO;
  /** 병사 탭에서 보는 병종(가지). 빈 글자는 모두. */
  private branch: string = '';

  constructor(root: HTMLElement, army: Army, onBack: () => void, onChange: () => void = () => {}) {
    this.root = root;
    this.army = army;
    this.onBack = onBack;
    this.onChange = onChange;
  }

  render(): void {
    clear(this.root);
    // 고른 동료가 더는 없으면(계정이 바뀌었다) 장수로.
    if (this.who !== HERO && !metCompanions(this.army).some((c) => c.id === this.who)) this.who = HERO;
    const body = this.tab === 'leaders' ? [this.leaders(), this.who === HERO ? this.skills() : null] : [this.soldiers()];
    this.root.append(this.header(), this.tabs(), ...body.filter((n): n is HTMLElement => n !== null));
  }

  /** 장수·동료 / 병사 탭. */
  private tabs(): HTMLElement {
    const tab = (id: StatsTab, label: string): HTMLButtonElement => {
      const on = this.tab === id;
      const b = withKey(
        button(label, () => {
          this.tab = id;
          this.touch(`tab:${id}`, undefined, false);
        }, `stat-tab${on ? ' on' : ''}`),
        `tab:${id}`,
      );
      b.setAttribute('aria-pressed', String(on));
      return b;
    };
    return el(
      'nav',
      { class: 'stat-tabs', 'aria-label': '볼 것' },
      tab('leaders', `장수·부하장수 ${1 + metCompanions(this.army).length}`),
      tab('soldiers', `병사 ${this.army.soldiers.length}`),
    );
  }

  /**
   * 장수 기술(Todo10): 들고 나갈 하나를 고르고, 장수 레벨 10마다 받는 기술 점수를 넣어 올린다. 다시 그린 뒤 누른 단추에
   * 초점을 돌린다 — 목록을 통째로 다시 그리면 키보드 초점이 길을 잃는다.
   */
  private skills(): HTMLElement {
    const a = this.army;
    const free = freeSkillPoints(a);
    const pick = skillForBattle(a).id;
    const total = skillPointsFor(heroLevelOf(a));
    const cards = SKILLS.map((d) => {
      const lv = skillLevelOf(a, d.id);
      const on = d.id === pick;
      const choose = withKey(
        button(on ? '들고 나간다 ✓' : '이것을 든다', () => {
          pickSkill(a, d.id);
          this.touch(`pick:${d.id}`);
        }, `mini${on ? ' on' : ''}`),
        `pick:${d.id}`,
      );
      choose.setAttribute('aria-pressed', String(on));
      // 잠긴 까닭을 글자로 적는다 — 폰에서는 제목을 볼 수 없다(QA).
      // "레벨 3로"는 조사가 틀린다(3으로) — 조사가 필요 없게 적는다.
      const upLabel = lv >= SKILL_LEVEL_MAX ? '끝까지 올렸다' : `레벨 ${lv} → ${lv + 1} ${free <= 0 ? '— 점수 없음' : '(1점)'}`;
      const up = withKey(
        button(upLabel, () => {
          if (raiseSkill(a, d.id)) this.touch(`up:${d.id}`, `pick:${d.id}`);
        }, 'mini'),
        `up:${d.id}`,
      );
      up.disabled = free <= 0 || lv >= SKILL_LEVEL_MAX;
      return el(
        'div',
        { class: `skill-card${on ? ' on' : ''}` },
        el('b', {}, `${d.name} ${d.hanja}`, el('span', { class: 'skill-lv' }, ` 레벨 ${lv}/${SKILL_LEVEL_MAX}`)),
        el('span', { class: 'dim' }, d.summary(lv)),
        el('div', { class: 'skill-acts' }, choose, up),
      );
    });
    const spent = spentSkillPoints(a);
    const resetLabel =
      spent === 0
        ? '점수 되돌리기 — 넣은 점수 없음'
        : a.gold < SKILL_RESET_GOLD
          ? `점수 되돌리기 — 냥이 모자란다(${SKILL_RESET_GOLD}냥)`
          : `점수 되돌리기 ${SKILL_RESET_GOLD}냥`;
    const reset = withKey(
      button(resetLabel, () => {
        if (resetSkills(a)) this.touch('reset', `pick:${pick}`);
      }, 'mini'),
      'reset',
    );
    reset.disabled = spent === 0 || a.gold < SKILL_RESET_GOLD;
    return el(
      'section',
      { class: 'panel stat-skills' },
      el(
        'div',
        { class: 'skill-head' },
        // "장수 기술 기술 점수"로 낱말이 겹쳐 읽혔다(QA).
        el('h2', {}, '장수 기술', el('small', {}, ` 점수 ${free}/${total} 남음 — 장수 레벨 ${SKILL_POINT_EVERY}마다 1점`)),
        reset,
      ),
      el('p', { class: 'dim' }, '전장의 기술 단추(R)는 하나다. 들고 나갈 기술을 고른다. 방진·일제사격은 전장에서 고른 분대에 건다.'),
      el('div', { class: 'skill-cards' }, ...cards),
    );
  }

  /**
   * 바꾼 것을 저장하고 다시 그린 뒤, 누른 단추로 초점을 돌린다. 그 단추가 잠겼으면(마지막 점수를 썼다, 레벨 5, 되돌린 뒤)
   * 대신 갈 곳(`fallback`)으로 — 잠긴 단추는 초점을 받지 못해 초점이 문서 밖(BODY)으로 빠졌다(리뷰·QA).
   */
  private touch(key: string, fallback?: string, changed = true): void {
    if (changed) this.onChange();
    // 다시 그리는 동안 스크롤을 지킨다(Todo11: "장수 기술을 업그레이드 해도 페이지가 최상단으로 스크롤되지 않도록").
    // 통째로 지우는 순간 내용 높이가 0이 되어 화면(#screen)이 맨 위로 당겨졌다. 되돌린 뒤 누른 단추가 전과 같은 높이에
    // 오도록 한 번 더 민다 — 장비 화면(`armory.ts`)과 같은 방식.
    const scroller = this.root.parentElement;
    const prevScroll = scroller?.scrollTop ?? 0;
    // 얼굴 칩 줄은 옆으로 민다 — 다시 그리면 맨 왼쪽으로 돌아가, 오른쪽 끝의 칩을 고르면 고른 칩이 줄 밖에 남았다
    // (리뷰·QA: 360 폭에서 넷째 칩이 13px만 보였다). 밀어 둔 자리를 되돌리고, 고른 칩은 줄 안으로 들인다.
    const prevWho = this.root.querySelector<HTMLElement>('.stat-who')?.scrollLeft ?? 0;
    const find = (k: string | undefined): HTMLButtonElement | null =>
      k ? this.root.querySelector<HTMLButtonElement>(`[data-key="${CSS.escape(k)}"]`) : null;
    const before = find(key)?.getBoundingClientRect().top;
    this.render();
    const own = find(key);
    const landed = own && !own.disabled ? own : (find(fallback) ?? own);
    landed?.focus({ preventScroll: true });
    const row = this.root.querySelector<HTMLElement>('.stat-who');
    if (row) {
      row.scrollLeft = prevWho;
      const on = row.querySelector<HTMLElement>('.stat-face.on');
      if (on) {
        const pad = 8;
        const left = on.offsetLeft - row.offsetLeft;
        if (left < row.scrollLeft + pad) row.scrollLeft = Math.max(0, left - pad);
        else if (left + on.offsetWidth > row.scrollLeft + row.clientWidth - pad) row.scrollLeft = left + on.offsetWidth - row.clientWidth + pad;
      }
    }
    if (!scroller) return;
    scroller.scrollTop = prevScroll;
    const after = landed?.getBoundingClientRect().top;
    if (before !== undefined && after !== undefined) scroller.scrollTop += after - before;
  }

  private header(): HTMLElement {
    return el(
      'header',
      { class: 'sortie-head' },
      el(
        'div',
        { class: 'brand' },
        el('span', { class: 'eyebrow' }, `ILDANGBAEK 일당백 · ${CULTURE_NAMES[this.army.culture]}`),
        el('h1', {}, '능력치'),
      ),
      el('div', { class: 'ledger' }, button('출정 준비로', this.onBack, 'primary')),
    );
  }

  /** 장수와 동료 — 한 사람씩 고른다. 착장을 반영한 몸이다(전장에 서는 그 몸). */
  private leaders(): HTMLElement {
    const a = this.army;
    const mates = metCompanions(a);
    const heroLv = heroLevelOf(a);
    const heroKey = iconKeyFor(dressLeader(a.culture, loadoutOf(a, HERO), heroLv));
    const mateKey = (c: CompanionDef): string => iconKeyFor(dressCompanion(c, companionLevelOf(a, c.id), loadoutOf(a, c.id), a.culture));
    // 얼굴 칩: 가슴 위 그림, 이름, 레벨. 글자 단추였을 때는 누가 누구인지 이름을 읽어야 알았다.
    const pickBtn = (id: string, label: string, key: string, level: number): HTMLButtonElement => {
      const on = this.who === id;
      const b = withKey(el('button', { type: 'button', class: `stat-face${on ? ' on' : ''}` }) as HTMLButtonElement, `who:${id}`);
      b.append(unitIcon(key, 'bust'), el('span', { class: 'face-name' }, label), el('span', { class: 'face-lv' }, `Lv ${level}`));
      b.addEventListener('click', () => {
        this.who = id;
        this.touch(`who:${id}`, undefined, false);
      });
      b.setAttribute('aria-pressed', String(on));
      // 키보드로 줄 밖의 칩에 가면 줄 안으로 들인다(QA: 360 폭에서 Tab으로 넷째 칩에 가면 가려진 채 초점만 갔다).
      b.addEventListener('focus', () => b.scrollIntoView({ block: 'nearest', inline: 'nearest' }));
      return b;
    };
    const mate = this.who === HERO ? undefined : mates.find((c) => c.id === this.who)!;
    // 부하장수의 열전(`COMPANION_LORE`): 누구인지 한 줄과 이야기. 플레이한 사람이 청했다(Todo11): "능력치 동료 섹션에
    // 각각의 동료 열전을 넣자." 편성 화면의 상세 창에만 있던 것을 여기서도 읽는다 — 능력치와 사람을 함께 본다.
    const lore = mate ? COMPANION_LORE[mate.id] : undefined;
    // 막대의 잣대: 장수와 만난 부하장수 가운데 능력치마다 가장 높은 값(간격은 가장 짧은 값).
    const bodies: Stats[] = [
      dressLeader(a.culture, loadoutOf(a, HERO), heroLv).stats,
      ...mates.map((c) => underCommand(dressCompanion(c, companionLevelOf(a, c.id), loadoutOf(a, c.id), a.culture), heroLv).stats),
    ];
    const ref = bestOf(bodies);
    const chosen = mate ? companionStatCard(a, mate, ref) : leaderStatCard(a, ref);
    // 무대: 전신 그림(세 배), 이름, 직함, 레벨과 다음 레벨까지의 막대.
    const canvas = el('canvas', { class: 'stat-portrait', width: '0', height: '0', 'aria-hidden': 'true' }) as HTMLCanvasElement;
    void drawUnitPortrait(canvas, mate ? mateKey(mate) : heroKey, 3, true);
    const level = mate ? companionLevelOf(a, mate.id) : heroLv;
    const prog = mate ? mateProgress(a, mate.id) : heroProgress(a).level;
    const stage = el(
      'div',
      { class: `stat-stage${mate ? '' : ' hero'}` },
      el('div', { class: 'stat-plinth' }, canvas),
      el(
        'div',
        { class: 'stat-id' },
        el('span', { class: 'stat-role' }, mate ? '부하장수' : `${CULTURE_NAMES[a.culture]}의 장수`),
        el('h2', {}, mate ? mate.name : '장수'),
        el('p', { class: 'stat-title' }, mate ? `${lore ? `${lore.who} · ` : ''}${mate.title}` : rankFor(a.totalMerit).name),
        el(
          'div',
          { class: 'stat-level' },
          el('b', {}, `Lv ${level}`),
          prog
            ? el(
                'div',
                { class: 'stat-xp', role: 'img', 'aria-label': `다음 레벨까지 ${prog.rest.toLocaleString()}` },
                el('i', { style: `width:${Math.max(2, Math.min(100, Math.round((prog.have * 100) / Math.max(1, prog.span))))}%` }),
              )
            : null,
          el('small', { class: 'dim' }, prog ? `다음 레벨까지 ${prog.rest.toLocaleString()}` : '가장 높은 레벨'),
        ),
      ),
    );
    return el(
      'section',
      { class: 'panel stat-leaders' },
      el(
        'div',
        { class: 'stat-who', role: 'group', 'aria-label': '볼 사람' },
        pickBtn(HERO, '장수', heroKey, heroLv),
        ...mates.map((c) => pickBtn(c.id, c.name, mateKey(c), companionLevelOf(a, c.id))),
      ),
      mates.length === 0 ? el('p', { class: 'dim' }, '부하장수는 관문 5·10·15를 넘으면 한 명씩 합류한다.') : null,
      el(
        'div',
        { class: 'stat-show' },
        el('div', { class: 'stat-side' }, stage, mate && lore ? el('blockquote', { class: 'stat-lore' }, lore.story) : null),
        el('div', { class: 'stat-cards one' }, chosen),
      ),
    );
  }

  /**
   * 병사 한 명씩. 병영과 같은 순서(`rosterOrder`)라 거기서 본 병사를 여기서 찾기 쉽다.
   * 정병 보정(`retinue`)이 든 값이다 — 같은 병종·레벨의 적병보다 체력과 공격이 높다.
   */
  private soldiers(): HTMLElement {
    const all = rosterOrder(this.army.soldiers);
    // 병종 칩: 가지마다 몇 명인지 보이고, 누르면 그 가지만 본다. 아흔 줄을 한 번에 훑기는 어렵다.
    const counts = new Map<string, number>();
    for (const s of all) {
      const b = TROOP_BY_ID.get(s.troop)!.branch;
      counts.set(b, (counts.get(b) ?? 0) + 1);
    }
    if (this.branch !== '' && !counts.has(this.branch)) this.branch = '';
    const chip = (id: string, label: string, n: number): HTMLButtonElement => {
      const on = this.branch === id;
      const b = withKey(el('button', { type: 'button', class: `stat-chip${on ? ' on' : ''}` }) as HTMLButtonElement, `branch:${id}`);
      if (id !== '' && hasBranchIcon(id)) b.append(branchIcon(id, 16));
      b.append(el('span', {}, label), el('b', {}, String(n)));
      b.addEventListener('click', () => {
        this.branch = id;
        this.touch(`branch:${id}`, undefined, false);
      });
      b.setAttribute('aria-pressed', String(on));
      return b;
    };
    const order: readonly Branch[] = [Branch.Spear, Branch.Blade, Branch.Bow, Branch.Horse, Branch.Levy];
    const list = this.branch === '' ? all : all.filter((s) => TROOP_BY_ID.get(s.troop)!.branch === this.branch);
    return el(
      'section',
      { class: 'panel stat-soldiers' },
      el('h2', {}, `병사 ${all.length}명`),
      el('p', { class: 'dim' }, '병사 자신의 몸이다. 전장에서는 여기에 선 분대의 보정(장수·부하장수 탭의 카드)이 더해진다.'),
      all.length === 0
        ? null
        : el(
            'div',
            { class: 'stat-chips', role: 'group', 'aria-label': '볼 병종' },
            chip('', '모두', all.length),
            ...order.filter((b) => counts.has(b)).map((b) => chip(b, BRANCH_FULL[b], counts.get(b)!)),
          ),
      all.length === 0
        ? el('p', { class: 'dim' }, '병사가 없다.')
        : el(
            'div',
            { class: 'stat-table-wrap' },
            el(
              'table',
              { class: 'stat-table' },
              el(
                'thead',
                {},
                el(
                  'tr',
                  {},
                  el('th', { class: 'who' }, '병사'),
                  el('th', {}, 'Lv'),
                  ...COLUMNS.map((c) => el('th', { title: c.title }, c.label)),
                ),
              ),
              el(
                'tbody',
                {},
                ...list.map((s) => {
                  const t = TROOP_BY_ID.get(s.troop)!;
                  const level = soldierLevel(s);
                  const p = levelProgress(s.xp);
                  // 전장에서의 체력을 적는다(`fieldHp`) — 병사는 전장에 설 때 체력이 더 붙는다. 걸음과 같은 까닭이다.
                  const raw = specForTroop(s.troop, level, true, s.uid, isElite(s)).stats;
                  const stats = { ...raw, hp: fieldHp(raw.hp) };
                  return el(
                    'tr',
                    {},
                    el(
                      'th',
                      { class: 'who', scope: 'row' },
                      el(
                        'span',
                        { class: `swho${isElite(s) ? ' elite' : ''}` },
                        unitIcon(iconKeyFor(specForTroop(s.troop)), 'bust'),
                        el(
                          'span',
                          { class: 'swho-text' },
                          // 용사는 이름 앞에 별 — 같은 병종·레벨인데 체력이 높은 까닭이 표에서 읽힌다(QA).
                          el('span', { class: `sname${isElite(s) ? ' elite' : ''}` }, isElite(s) ? `★ ${s.name}` : s.name),
                          el('span', { class: 'stroop' }, `T${t.tier} ${t.name}`),
                        ),
                      ),
                    ),
                    el(
                      'td',
                      { class: 'slv', title: p.span > 0 ? `다음 레벨까지 ${p.span - p.into}` : '최고 레벨' },
                      el('span', { class: 'lv-pill' }, String(level)),
                    ),
                    ...COLUMNS.map((c) => el('td', {}, c.value(stats))),
                  );
                }),
              ),
            ),
          ),
    );
  }
}

/**
 * 장수 카드. 능력치 화면과 편성 화면의 상세(카드 누르기)가 같이 쓴다 — 두 곳이 따로 셈하면
 * 한쪽만 고치는 날 서로 다른 숫자를 말한다.
 */
export function leaderStatCard(a: Army, ref?: Stats): HTMLElement {
  const lv = heroLevelOf(a);
  const heroWorn = loadoutOf(a, HERO);
  const hero = dressLeader(a.culture, heroWorn, lv);
  return card('장수', `${rankFor(a.totalMerit).name} · 레벨 ${lv}`, hero.stats, [
    `직할대에 걸리는 보정: ${bonusText(hero.captain)}`,
    // 위 값에 이미 든 장수 레벨 몫이다. 따로 적기만 하면 직할대에 한 번 더 더하는 것처럼
    // 읽혔다(리뷰가 짚었다). 동료 분대에도 같은 몫이 걸린다는 것을 알리는 줄이다.
    `${commandText(lv)} (위 값에 들어 있다)`,
    heroWeaponLine(heroWorn),
    wornShieldLine(heroWorn),
    !isMounted(heroWorn) ? '말이 없어 걷는다' : '',
    salaryLine(a),
  ], ref);
}

/**
 * 월급 한 줄: 언제, 얼마를, 어떤 조건으로 받는가(`salarySteps`). 편성의 칩에는 온 몫과 그 깊이만 들어가고 나머지(얕은
 * 곳의 몫, 없는 경우)는 여기 적는다. 플레이한 사람: "이런 내용 플레이어가 알 수 있게 어딘가에서 알려줘."
 */
export function salaryLine(a: Army): string {
  const steps = salarySteps(a);
  const never = '장수가 쓰러진 출정, 도적 토벌, 영토 공격에는 없다.';
  if (steps.length === 0) {
    // 편장: 아직 없다. 언제부터 생기는지를 적는다.
    const next = RANKS[RANKS.indexOf(rankFor(a.totalMerit)) + 1];
    // 깊이는 관등의 깊이와 최고 깊이의 문턱 가운데 깊은 쪽이다(`salarySteps`와 같은 셈) — 처음에는 관등의 깊이(6)만
    // 적어, 깊이 22까지 가 본 편장에게 "깊이 6 이상"이라고 했다(실제 문턱은 13, 리뷰·QA).
    const from = Math.max(salaryFloor(a), SALARY_DEPTH[1] ?? 1);
    return `월급: 아직 없다 — ${next ? `${next.name}부터` : '관등이 오르면'} 출정을 철수로 마칠 때 받는다(깊이 ${from} 이상에서 이긴 판이 있어야 한다). 그 출정으로 관등이 오르면 오른 관등으로 받는다.`;
  }
  const list = steps.map((s) => `깊이 ${s.from} 이상 ${s.pay.toLocaleString()}냥`).join(', ');
  const last = steps[steps.length - 1].from;
  return `월급: 출정을 철수로 마칠 때 한 번 받는다. 그 출정에서 이긴 가장 깊은 판으로 정한다 — ${list}. 깊이 ${last}보다 얕은 곳에서만 이겼으면 없다. 그 출정으로 관등이 오르면 오른 관등으로 받는다. ${never}`;
}

/**
 * 방패 한 줄: 막는 수와 든 동안의 벌점(`SHIELD_PENALTY`). 병사·장수·부하장수 카드가 같이 쓴다. 벌점까지 적어야 부서지는
 * 것이 손해만은 아님이 읽힌다. 방패가 없으면(0발) 빈 줄.
 *
 * 처음에는 병사 카드에만 있었다 — 장수와 부하장수는 방패를 차도 전장에서 화살을 막는다는 말이 어디에도 없었다(리뷰).
 */
export function shieldLine(blocks: number, item = ''): string {
  if (blocks <= 0) return '';
  // 물건 이름은 쌍점 뒤에 — 앞에 두면(`방패(목순): …`) 괄호 때문에 이름표로 갈리지 않는다(`noteParts`).
  return `방패: ${item ? `${item} — ` : ''}앞에서 오는 화살 ${blocks}발을 막고 부서진다. 든 동안 공격력 −${100 - SHIELD_PENALTY.atkPct}%·공격 간격 +${SHIELD_PENALTY.cdPct - 100}%.`;
}

/** 장수·부하장수가 찬 방패의 줄. 막는 수는 전장과 같은 셈(`shieldBlocks` — 강화한 몫이 들어 있다). */
function wornShieldLine(worn: Loadout): string {
  return shieldLine(shieldBlocks(worn), equipOf(worn.shield)?.name);
}

/**
 * 사거리는 무기가 정한다(`WEAPONS`). 무기 아이템이 없으면 칼(`HERO_WEAPON`)을 든다 — 무기를 바꾸면 사거리가 왜 바뀌는지
 * 읽히게 무엇을 들었는지 적는다.
 */
export function heroWeaponLine(worn: Loadout): string {
  const held = worn.weapon ? equipOf(worn.weapon) : undefined;
  // 말 위의 활은 쓰지 않는다(`fieldLoadout`) — 맨손 칼로 싸운다. 적지 않으면 활을 들렸는데 왜 쏘지 않는지 알 길이 없다.
  const unused = held !== undefined && holdsBow(worn) && !shoots(worn);
  const item = unused ? undefined : held;
  const kind = item?.weapon ?? HERO_WEAPON;
  // 물건 이름이 무기 이름과 같으면(맥궁) 괄호를 달지 않는다 — "맥궁(맥궁)"으로 읽혔다(QA).
  const base = `무기: ${WEAPONS[kind].name}${item && item.name !== WEAPONS[kind].name ? `(${item.name})` : ''} — ${WEAPONS[kind].note} 사거리·공격 성격은 무기가 정한다.`;
  if (unused) return `${base} ${held.name}은 말 위에서 쏘지 못한다 — 말을 벗겨야 쏜다.`;
  // 활을 든 영웅의 규칙(`World`): 병사 궁수의 화살통(영웅은 최고 등급의 것), 서야 쏜다, 붙으면 활을 놓는다.
  if (shoots(worn)) return `${base} 화살 ${arrowsFor(HERO_ARROW_TIER)}발. 서 있을 때만 쏘고 휩쓸지 않는다. 적이 붙거나 화살이 떨어지면 활을 놓고 싸운다.`;
  return base;
}

/** 영웅의 화살통 등급. 영웅의 스펙 등급(5)은 표 밖이라 표의 마지막(4등급) 값을 받는다(`arrowsFor`). */
const HERO_ARROW_TIER = 4;

/** 동료 카드. 장수 레벨의 지휘 몫(`underCommand`)과 동료 레벨·착장이 든 몸이다. */
export function companionStatCard(a: Army, c: CompanionDef, ref?: Stats): HTMLElement {
  const lv = heroLevelOf(a);
  const level = companionLevelOf(a, c.id);
  const toNext = companionXpToNext(a, c.id);
  const st = a.companions.find((x) => x.id === c.id);
  const resting = st !== undefined && st.readyAt > a.sortieCount;
  const worn = loadoutOf(a, c.id);
  const mate = underCommand(dressCompanion(c, level, worn, a.culture), lv);
  return card(c.name, `${c.title} · 레벨 ${level}`, mate.stats, [
    `휘하에 걸리는 보정: ${bonusText(mate.captain)}`,
    // 이 사람만의 특성(Todo11, `MATE_TRAITS`): 잘 부리는 병종과 레벨이 오를 때 지휘가 기우는 쪽.
    traitLine(c, level),
    // 고유 기술(Todo10, `sim/mate-skills.ts`). 단추가 없어 여기 적지 않으면 있는 줄 모른다.
    mateSkillLine(c.id, level),
    // 장수와 같이 철수해 확정한 전과가 경험치다.
    toNext > 0 ? `다음 레벨까지 전과 ${toNext.toLocaleString()} — 함께 나가 살아 돌아오면 장수와 같은 몫을 받는다` : '가장 높은 레벨',
    heroWeaponLine(worn),
    wornShieldLine(worn),
    resting ? `요양 중 — 출정 ${st!.readyAt - a.sortieCount}번 뒤 복귀` : '',
    // 영토의 수비 대장(Todo9)이면 그 땅을 적는다.
    st?.garrison ? `${TERRITORY_BY_ID.get(st.garrison)?.name ?? st.garrison} 수비 대장 — 출정에 나서지 않는다` : '',
    !isMounted(worn) ? '말이 없어 걷는다' : '',
  ], ref);
}

/** 부하장수의 레벨 막대: 이 레벨에서 번 몫, 한 레벨의 크기, 남은 몫. 끝 레벨이면 null. 장수와 같은 표(`heroLevelMerit`)다. */
function mateProgress(a: Army, id: string): { have: number; span: number; rest: number } | null {
  const rest = companionXpToNext(a, id);
  if (rest <= 0) return null;
  const lv = companionLevelOf(a, id);
  const span = heroLevelMerit(lv + 1) - heroLevelMerit(lv);
  return { have: Math.max(0, span - rest), span, rest };
}

/** 능력치마다 가장 좋은 값(막대의 잣대). 공격 간격만은 가장 짧은 값이다. */
function bestOf(list: readonly Stats[]): Stats {
  const out = { ...list[0] };
  for (const s of list) {
    for (const k of Object.keys(out) as (keyof Stats)[]) {
      const v = s[k];
      if (typeof v !== 'number') continue;
      (out[k] as number) = k === 'cd' ? Math.min(out[k] as number, v) : Math.max(out[k] as number, v);
    }
  }
  return out;
}

/** 병사 칩의 가지 이름(`BRANCH_NAMES`는 한 글자라 칩에 쓰기 짧다). */
const BRANCH_FULL: Record<Branch, string> = {
  [Branch.Levy]: '민병',
  [Branch.Spear]: '창병',
  [Branch.Blade]: '도검병',
  [Branch.Bow]: '궁병',
  [Branch.Horse]: '기병',
};

/** 동료 고유 기술 한 줄: 이름·레벨·설명, 다음 레벨이 오르는 동료 레벨. */
export function mateSkillLine(id: string, companionLevel: number): string {
  const def = MATE_SKILL_BY_ID.get(id);
  if (!def) return '';
  const lv = mateSkillLevel(companionLevel);
  const next = MATE_SKILL_LEVELS[lv - 1];
  return `고유 기술 「${def.name}」 레벨 ${lv}/${MATE_SKILL_LEVEL_MAX} — ${mateSkillText(def, lv)}${next !== undefined ? ` 기술은 부하장수 레벨 ${next}에 오른다.` : ''}`;
}

/** 천분율을 백분율 글로(소수 한 자리, .0은 뗀다). 레벨 몫이 천분율이 된 뒤로 2.4% 같은 값이 나온다. */
function pct(permille: number): string {
  return (permille / 10).toFixed(1).replace(/\.0$/, '');
}

/** 병사 한 명의 몸(정병 보정이 든 값). 표의 한 줄과 같은 숫자다. */
export function soldierStatCard(s: Soldier): HTMLElement {
  const t = TROOP_BY_ID.get(s.troop)!;
  const level = soldierLevel(s);
  const g = soldierGrowth(s.uid, level);
  // 상세 창 머리에 이름이 이미 있다. 카드 머리는 무엇을 보는지만 적는다.
  // 체력은 전장에서의 값이다(`fieldHp`) — 표와 같은 숫자여야 한다. 처음에는 표만 고쳐 이 카드가 92, 표가 110이었다(리뷰).
  const raw = specForTroop(s.troop, level, true, s.uid, isElite(s)).stats;
  return card('능력치', `${isElite(s) ? '용사 · ' : ''}T${t.tier} ${t.name} · 레벨 ${level}`, { ...raw, hp: fieldHp(raw.hp) }, [
    ...(isElite(s) ? [`용사의 몸: 체력 +${ELITE.hpPct - 100}% · 공격 +${ELITE.atkPct - 100}% · 이긴 판에서 쓰러져도 열에 ${ELITE.woundedPct / 10}은 돌아온다`] : []),
    // 레벨마다 굴린 몫의 합. 등급과 막대로 보인다(`growthNote`) — 처음에는 "체력 +181% · 공격 +150.4% (보통 +183% ·
    // +146.4%)" 한 줄이라 네 숫자를 암산으로 견줘야 했다(플레이한 사람).
    growthNote(g, level),
    // 사거리는 병종이 아니라 무기에서 나온다(`weaponRange`). 등급이 올라 더 닿는 몫(숙련)을 따로 적는다.
    weaponLine(t.weapon, t.tier),
    // 방패(`SHIELD_BY_TIER`)는 무기 바로 밑에 — 맨 아래에 두었더니 창을 내려야 보여 있는 줄 몰랐다.
    shieldLine(shieldFor(t.branch, t.tier)),
    // 병종 본능은 명령 없이 걸린다. 알려야 궁수 곁에 창을 두는 편성을 고른다.
    BRANCH_INSTINCT[t.branch] ?? '',
    // 화살통(`ARROWS_BY_TIER`). 바닥나면 어떻게 싸우는지 알아야 궁수를 얼마나 데려갈지 정한다.
    t.branch === Branch.Bow
      ? `화살 ${arrowsFor(t.tier)}발. 적이 붙으면 활을 놓고 싸운다. 다 쏘면 이기는 판은 근접, 지는 판은 물러난다.`
      : '',
    '전장에서는 여기에 선 분대의 보정이 더해진다.',
  ]);
}

/**
 * 레벨 성장 한 칸: 등급("좋음 · 상위 22%")과, 체력·공격마다 평균을 가운데 둔 막대 위의 자리, 평균과의 차이.
 * 굴린 적이 없는 레벨 1은 줄이 없다.
 */
function growthNote(g: { hp: number; atk: number }, level: number): Note {
  const grade = growthGrade(g, level);
  if (!grade) return '';
  const row = (name: string, s: GrowthStat): HTMLElement => {
    // 평균에서 조금 벌어진 것(흩어짐의 0.3배 안)은 색을 입히지 않는다 — 처음에는 −1%p도 붉게 칠해 보통인 병사가 나빠 보였다.
    const side = s.z >= 0.3 ? 'up' : s.z <= -0.3 ? 'down' : 'same';
    return el(
      'div',
      { class: 'grow-row' },
      el('span', { class: 'grow-name' }, name),
      el('b', { class: 'grow-got' }, `+${pct(s.got)}%`),
      // 가운데 눈금이 같은 레벨의 평균, 점이 이 병사다.
      el('i', { class: 'grow-track', 'aria-hidden': 'true' }, el('b', { class: `grow-dot ${side}`, style: `left:${s.at.toFixed(1)}%` })),
      el('span', { class: `grow-gap ${side}` }, gapText(s.got, s.mean)),
    );
  };
  return [
    '레벨 성장',
    el(
      'div',
      { class: 'grow' },
      el(
        'div',
        { class: 'grow-head' },
        el('b', { class: `grow-grade ${grade.tone}` }, grade.label),
        // 덜 자란 병사(아쉬움·나쁨)만 "하위"로 적는다 — 보통인 병사에게 "하위 43%"는 나쁜 말로 읽혔다.
        // 순위는 정규 분포에 댄 어림이다("약"). 굴린 횟수가 적으면 같은 값인 병사가 많아 순위가 뜻이 없다 — 레벨 2에서
        // 둘 다 가장 좋게 굴린 병사는 아홉에 하나인데 "상위 4%"로 나왔다(리뷰). 그때는 등급만 적는다.
        el(
          'span',
          { class: 'dim' },
          level - 1 < GRADE_RANK_FROM
            ? '레벨이 낮아 아직 어림이다'
            : grade.tone === 'low' || grade.tone === 'poor'
              ? `같은 레벨 가운데 하위 약 ${100 - grade.topPct}%`
              : `같은 레벨 가운데 상위 약 ${grade.topPct}%`,
        ),
      ),
      row('체력', grade.hp),
      row('공격', grade.atk),
      el('p', { class: 'grow-foot dim' }, '레벨이 오를 때마다 체력 2~4%·공격 1.4~3.4%가 붙는다. 가운데 눈금이 보통이다.'),
    ),
  ];
}

function weaponLine(w: Weapon, tier: number): string {
  const d = WEAPONS[w];
  const skill = d.perTier * Math.max(0, tier - d.baseTier);
  return `무기: ${d.name} — 사거리 ${d.reach}${skill > 0 ? ` + 숙련 ${skill}` : ''}. ${d.note}`;
}

/**
 * 분대에 실제로 걸리는 보정. 상한(`clampCaptain`)까지 건 값이다 — 전장은 `World.setCaptain`에서
 * 같은 상한을 건다. 상한 전 값을 적으면 장비를 겹쳐 입은 사람에게 붙지 않는 몫을 보인다.
 */
/** 부하장수의 특성 한 줄: "잘 부리는 병종: 기병 공격 +8% (레벨이 오르면 +20%까지) · 자랄수록 공격이 더 는다". */
function traitLine(c: CompanionDef, level: number): string {
  const now = favorAt(c.trait, level);
  const top = favorAt(c.trait, LEVEL_MAX);
  const more = favorText(top) === favorText(now) ? '' : ` (레벨이 오르면 ${favorText(top).replace(/^\S+ \S+ /, '')}까지)`;
  const l = c.trait.lean;
  const lean = l.defEvery < 13 ? '방어가 더 자주 오른다' : l.atk > l.grit ? '공격이 더 는다' : l.grit > l.atk ? '사기가 더 는다' : '공격과 사기가 고르게 는다';
  return `잘 부리는 병종: ${favorText(now)}${more} — 그 부대의 그 병종 병사에게만. 자랄수록 ${lean}.`;
}

function bonusText(c: CaptainBonus | undefined): string {
  const b = clampCaptain(c ?? NO_CAPTAIN);
  const parts: string[] = [];
  if (b.atkPct !== 100) parts.push(`공격 ${b.atkPct - 100}%`);
  if (b.defBonus > 0) parts.push(`방어 +${b.defBonus}`);
  if (b.gritBonus > 0) parts.push(`사기 +${b.gritBonus}`);
  return parts.length > 0 ? parts.join(' · ') : '없음';
}

/**
 * 설명 한 줄을 이름표와 내용으로 가른다: "무기: 칼 — …" → ['무기', '칼 — …']. 이름표가 없는 줄은 ['', 줄].
 * 줄글을 그대로 늘어놓으면 대여섯 줄이 한 덩어리로 보여, 무엇에 관한 줄인지 앞머리를 읽어야 알았다.
 */
export function noteParts(line: string): [string, string] {
  const skill = /^고유 기술 (.+)$/.exec(line);
  if (skill) return ['고유 기술', skill[1]];
  const m = /^([가-힣A-Za-z ·]{1,14}): (.+)$/.exec(line);
  return m ? [m[1], m[2]] : ['', line];
}

/** 막대의 길이(%). 잣대가 0이면 막대가 없다(-1). 공격 간격은 짧을수록 길다. */
function barPct(key: string, s: Stats, ref: Stats): number {
  const v = s[key as keyof Stats];
  const r = ref[key as keyof Stats];
  if (typeof v !== 'number' || typeof r !== 'number' || r <= 0 || v <= 0) return v === 0 && typeof r === 'number' && r > 0 ? 0 : -1;
  const pct = key === 'cd' ? (r * 100) / v : (v * 100) / r;
  return Math.max(4, Math.min(100, Math.round(pct)));
}

/** 설명 한 줄: 글("이름표: 내용"으로 가른다 — `noteParts`)이거나, 이름표와 미리 만든 내용. */
type Note = string | [string, HTMLElement];

function card(name: string, sub: string, s: Stats, notes: Note[], ref?: Stats): HTMLElement {
  const lines: [string, string | HTMLElement][] = notes.filter((n) => n !== '').map((n) => (typeof n === 'string' ? noteParts(n) : n));
  return el(
    'div',
    { class: 'stat-card' },
    el('div', { class: 'stat-card-head' }, el('b', {}, name), el('span', { class: 'dim' }, sub)),
    el(
      'dl',
      { class: 'stat-tiles' },
      ...COLUMNS.map((c) => {
        const pct = ref ? barPct(c.key, s, ref) : -1;
        return el(
          'div',
          { class: `stat-tile k-${c.key}`, title: c.title },
          el('dt', {}, c.label),
          el('dd', {}, c.value(s)),
          pct >= 0 ? el('i', { class: `tile-bar${pct >= 100 ? ' top' : ''}`, 'aria-hidden': 'true' }, el('b', { style: `width:${pct}%` })) : null,
        );
      }),
    ),
    ref ? el('p', { class: 'stat-tiles-note dim' }, '막대는 장수와 부하장수 가운데 가장 높은 값에 견준 길이다(금빛이 으뜸, 간격은 짧을수록 길다).') : null,
    lines.length > 0
      ? el(
          'ul',
          { class: 'stat-notes' },
          // 미리 만든 내용(레벨 성장의 막대)은 이름표 아래에 온 폭으로 놓는다 — 이름표 옆 칸은 상세 창에서 187px뿐이라 넘쳤다(리뷰).
          ...lines.map(([k, v]) => el('li', typeof v === 'string' ? {} : { class: 'note-wide' }, k !== '' ? el('span', { class: 'note-k' }, k) : null, el('span', { class: 'note-v' }, v))),
        )
      : null,
  );
}
